import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Users, UserX, PlusCircle as Plus, Trophy, Target, Calendar, MessageSquare, ArrowRightLeft } from 'lucide-react';
import ConfirmDialog from '../ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlayerManagementCard = ({ selectedTeam, allTeams = [], onShowInvitePlayer, onRefreshTeams }) => {
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showPlayerDetails, setShowPlayerDetails] = useState(false);
  const [showCreateChallenge, setShowCreateChallenge] = useState(false);
  const [showSendMessage, setShowSendMessage] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferPlayer, setTransferPlayer] = useState(null);
  const [transferDestination, setTransferDestination] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [playerProgress, setPlayerProgress] = useState(null);
  const [playerChallenges, setPlayerChallenges] = useState([]);
  const [playerMessages, setPlayerMessages] = useState([]);
  const [playerActivities, setPlayerActivities] = useState([]);
  const [showPlayerActivitiesModal, setShowPlayerActivitiesModal] = useState(false);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    description: '',
    onConfirm: null,
    variant: 'default',
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });

  // Form states
  const [challengeData, setChallengeData] = useState({
    title: '',
    description: '',
    category: 'skill_development',
    difficulty: 'medium',
    points_reward: 20,
    due_date: ''
  });
  const [messageData, setMessageData] = useState({
    message: '',
    message_type: 'motivational'
  });

  useEffect(() => {
    if (selectedTeam) {
      loadTeamPlayers();
    }
  }, [selectedTeam]);

  const loadTeamPlayers = async () => {
    if (!selectedTeam) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/coach/team/${selectedTeam.id}/players`);
      setPlayers(response.data.players);
    } catch (error) {
      console.error('Failed to load team players:', error);
    } finally {
      setLoading(false);
    }
  };

  const openPlayerDetails = async (player) => {
    setSelectedPlayer(player);
    setLoading(true);
    try {
      const [progressRes, challengesRes, messagesRes] = await Promise.all([
        axios.get(`${API}/coach/player/${player.id}/progress`),
        axios.get(`${API}/coach/player/${player.id}/challenges`),
        axios.get(`${API}/coach/player/${player.id}/messages`)
      ]);
      
      setPlayerProgress(progressRes.data);
      setPlayerChallenges(challengesRes.data);
      setPlayerMessages(messagesRes.data);
      setShowPlayerDetails(true);
    } catch (error) {
      toast.error('Failed to load player details');
    } finally {
      setLoading(false);
    }
  };


  const loadPlayerActivities = async () => {
    if (!selectedPlayer) return;
    
    setActivitiesLoading(true);
    try {
      // For now, use the player's recent activity from playerProgress
      // Or fetch from a specific endpoint if available
      if (playerProgress?.recent_responses) {
        setPlayerActivities(playerProgress.recent_responses);
        setShowPlayerActivitiesModal(true);
      } else {
        toast.info('No activity history available for this player');
      }
    } catch (error) {
      console.error('Error loading player activities:', error);
      toast.error('Failed to load activity history');
    } finally {
      setActivitiesLoading(false);
    }
  };


  const removePlayer = async (playerId, playerName) => {
    // Skip confirmation dialog (blocked in sandboxed environment)
    try {
      await axios.delete(`${API}/coach/team/${selectedTeam.id}/player/${playerId}`);
      toast.success(`${playerName} has been removed from the team`);
      loadTeamPlayers();
      onRefreshTeams();
    } catch (error) {
      toast.error('Failed to remove player');
    }
  };

  const openTransferModal = (player) => {
    setTransferPlayer(player);
    setTransferDestination('');
    setShowTransferModal(true);
  };

  const handleTransferPlayer = async () => {
    if (!transferDestination) {
      toast.error('Please select a destination team');
      return;
    }
    
    setTransferring(true);
    try {
      const response = await axios.post(
        `${API}/coach/team/${selectedTeam.id}/player/${transferPlayer.id}/transfer`,
        { destination_team_id: transferDestination }
      );
      toast.success(response.data.message);
      setShowTransferModal(false);
      setTransferPlayer(null);
      setTransferDestination('');
      loadTeamPlayers();
      onRefreshTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer player');
    } finally {
      setTransferring(false);
    }
  };

  // Get other teams for transfer dropdown (exclude current team)
  const otherTeams = allTeams.filter(t => t.id !== selectedTeam?.id);

  const createChallenge = async () => {
    if (!challengeData.title || !challengeData.description) {
      toast.error('Please fill in title and description');
      return;
    }

    try {
      await axios.post(`${API}/coach/individual-challenge`, {
        player_id: selectedPlayer.id,
        ...challengeData
      });
      
      toast.success('Individual challenge created successfully!');
      setChallengeData({
        title: '',
        description: '',
        category: 'skill_development',
        difficulty: 'medium',
        points_reward: 20,
        due_date: ''
      });
      setShowCreateChallenge(false);
      
      // Refresh player challenges
      const challengesRes = await axios.get(`${API}/coach/player/${selectedPlayer.id}/challenges`);
      setPlayerChallenges(challengesRes.data);
    } catch (error) {
      toast.error('Failed to create challenge');
    }
  };

  const sendMessage = async () => {
    if (!messageData.message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      await axios.post(`${API}/coach/message`, {
        player_id: selectedPlayer.id,
        ...messageData
      });
      
      toast.success('Message sent successfully!');
      setMessageData({ message: '', message_type: 'motivational' });
      setShowSendMessage(false);
      
      // Refresh player messages
      const messagesRes = await axios.get(`${API}/coach/player/${selectedPlayer.id}/messages`);
      setPlayerMessages(messagesRes.data);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  if (!selectedTeam) {
    return (
      <Card className="border-0 shadow-lg bg-blue-900/95 backdrop-blur-sm border border-blue-700">
        <CardContent className="text-center py-8">
          <p className="text-yellow-400">Select a team to manage players</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-0 shadow-lg bg-blue-900/95 backdrop-blur-sm border border-blue-700">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-white">Player Management - {selectedTeam.name}</CardTitle>
            <Button onClick={onShowInvitePlayer} size="sm" className="bg-yellow-500 hover:bg-emerald-600">
              <Plus className="w-4 h-4 mr-2" />
              Invite Player
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-pulse text-yellow-400">Loading players...</div>
            </div>
          ) : players.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-yellow-400">No players on this team yet</p>
              <Button onClick={onShowInvitePlayer} className="mt-4 bg-yellow-500 hover:bg-emerald-600">
                Invite Your First Player
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {players.map((player) => (
                <div key={player.id} className="p-4 border border-blue-700 rounded-lg bg-blue-900">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <Avatar className="w-10 h-10 rounded-lg">
                          {player.profile_image ? (
                            <AvatarImage 
                              src={`${API}/user/${player.id}/profile-image?t=${Date.now()}`}
                              alt={player.full_name}
                              className="rounded-lg"
                            />
                          ) : null}
                          <AvatarFallback className="bg-emerald-100 text-slate-50 rounded-lg">
                            {player.full_name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h4 className="font-semibold text-white">{player.full_name}</h4>
                          <p className="text-sm text-yellow-400">@{player.username} • {player.primary_position || 'Position not set'}</p>
                          <div className="flex items-center space-x-4 mt-1">
                            <span className="text-xs text-yellow-400">Level {player.level}</span>
                            <span className="text-xs text-yellow-400">{player.total_points} pts</span>
                            <span className="text-xs text-yellow-400">
                              {player.recent_activity_count} recent activities
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-3 flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <Trophy className="w-4 h-4 text-yellow-500" />
                          <span className="text-sm text-slate-50">{player.completed_challenges} completed</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Target className="w-4 h-4 text-blue-500" />
                          <span className="text-sm text-slate-50">{player.pending_challenges} pending</span>
                        </div>
                        {player.unread_messages > 0 && (
                          <Badge className="bg-red-100 text-red-800">
                            {player.unread_messages} unread messages
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col space-y-2">
                      <Button 
                        size="sm" 
                        onClick={() => openPlayerDetails(player)}
                        className="bg-yellow-500 hover:bg-emerald-600"
                      >
                        Manage Player
                      </Button>
                      {otherTeams.length > 0 && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openTransferModal(player)}
                          className="border-blue-400 text-blue-400 hover:bg-blue-900"
                          data-testid={`transfer-player-${player.id}`}
                        >
                          <ArrowRightLeft className="w-3 h-3 mr-1" />
                          Transfer
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setConfirmDialog({
                          open: true,
                          title: 'Remove Player?',
                          description: `Are you sure you want to remove ${player.full_name} from ${selectedTeam.name}? They will no longer have access to team activities.`,
                          confirmText: 'Remove',
                          cancelText: 'Cancel',
                          variant: 'destructive',
                          onConfirm: () => removePlayer(player.id, player.full_name)
                        })}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transfer Player Modal */}
      {showTransferModal && transferPlayer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 rounded-lg max-w-md w-full border border-blue-700 shadow-2xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <ArrowRightLeft className="w-5 h-5 text-blue-400" />
                  Transfer Player
                </h2>
                <Button 
                  onClick={() => setShowTransferModal(false)}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-blue-800"
                >
                  ✕
                </Button>
              </div>
              
              <div className="mb-4 p-3 bg-blue-800/50 rounded-lg">
                <p className="text-slate-300 text-sm">
                  Transfer <span className="font-semibold text-white">{transferPlayer.full_name}</span> from{' '}
                  <span className="text-yellow-400">{selectedTeam.club_name} - {selectedTeam.name}</span>
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-white mb-2 block">Destination Team</Label>
                  <Select value={transferDestination} onValueChange={setTransferDestination}>
                    <SelectTrigger className="bg-blue-800 border-blue-600 text-white" data-testid="transfer-destination-select">
                      <SelectValue placeholder="Select a team..." />
                    </SelectTrigger>
                    <SelectContent className="bg-blue-800 border-blue-600">
                      {otherTeams.map((team) => (
                        <SelectItem 
                          key={team.id} 
                          value={team.id}
                          className="text-white hover:bg-blue-700"
                        >
                          {team.club_name} - {team.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1 border-slate-500 text-slate-300 hover:bg-blue-800"
                    onClick={() => setShowTransferModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                    onClick={handleTransferPlayer}
                    disabled={!transferDestination || transferring}
                    data-testid="confirm-transfer-btn"
                  >
                    {transferring ? 'Transferring...' : 'Transfer Player'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Player Details Modal */}
      {showPlayerDetails && selectedPlayer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-blue-700 shadow-2xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {selectedPlayer.full_name} - Player Management
                </h2>
                <Button 
                  onClick={() => setShowPlayerDetails(false)}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-blue-800"
                >
                  ✕
                </Button>
              </div>

              <Tabs defaultValue="progress" className="space-y-4">
                <TabsList className="grid w-full grid-cols-4 bg-blue-950 border-blue-700">
                  <TabsTrigger value="progress" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-blue-900 text-white">Progress</TabsTrigger>
                  <TabsTrigger value="challenges" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-blue-900 text-white">Challenges</TabsTrigger>
                  <TabsTrigger value="messages" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-blue-900 text-white">Messages</TabsTrigger>
                  <TabsTrigger value="actions" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-blue-900 text-white">Actions</TabsTrigger>
                </TabsList>

                <TabsContent value="progress" className="space-y-4">
                  {playerProgress && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Card className="bg-blue-900/90 border-blue-700">
                        <CardContent className="p-4">
                          <div className="text-center">
                            <Trophy className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                            <p className="text-lg font-bold text-white">{playerProgress?.total_points || 0}</p>
                            <p className="text-sm text-yellow-400">Total Points</p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="bg-blue-900/90 border-blue-700">
                        <CardContent className="p-4">
                          <div className="text-center">
                            <Target className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                            <p className="text-lg font-bold text-white">{playerProgress?.level || 1}</p>
                            <p className="text-sm text-yellow-400">Current Level</p>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="bg-blue-900/90 border-blue-700">
                        <CardContent className="p-4">
                          <div className="text-center">
                            <Calendar className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                            <p className="text-lg font-bold text-white">
                              {playerProgress?.recent_activity_count || 0}
                            </p>
                            <p className="text-sm text-yellow-400">Recent Activities (30 days)</p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                  
                  {/* Activities Button */}
                  <div className="text-center">
                    <Button 
                      className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                      onClick={loadPlayerActivities}
                      disabled={activitiesLoading}
                    >
                      {activitiesLoading ? 'Loading...' : 'View Recent Activity'}
                    </Button>
                  </div>
                  
                  {playerProgress && playerProgress.recent_responses && playerProgress.recent_responses.length > 0 && (
                    <Card className="bg-blue-900/90 border-blue-700">
                      <CardHeader>
                        <CardTitle className="text-white">Recent Activity</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {playerProgress.recent_activity.slice(0, 10).map((activity) => (
                            <div key={activity.id || `${activity.type}-${activity.completed_at}`} className="flex justify-between items-center p-2 bg-blue-800 rounded border border-blue-700">
                              <div>
                                <p className="text-sm font-medium text-white">
                                  {activity.type === 'daily_challenge' ? 
                                    `Daily Challenge - ${activity.category}` : 
                                    activity.title || 'Individual Challenge'}
                                </p>
                                <p className="text-xs text-yellow-400">
                                  {new Date(activity.completed_at).toLocaleDateString()}
                                </p>
                              </div>
                              <Badge className="bg-yellow-500 text-blue-900">
                                +{activity.points_earned} pts
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="challenges" className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-white">Individual Challenges</h3>
                    <Button 
                      onClick={() => setShowCreateChallenge(true)}
                      size="sm"
                      className="bg-yellow-500 hover:bg-yellow-600 text-blue-900"
                    >
                      Create Challenge
                    </Button>
                  </div>
                  
                  <div className="space-y-3">
                    {playerChallenges.map((challenge) => (
                      <Card key={challenge.id} className="bg-blue-900/90 border-blue-700">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-semibold text-white">{challenge.title}</h4>
                              <p className="text-sm text-yellow-400 mt-1">{challenge.description}</p>
                              <div className="flex items-center space-x-2 mt-2">
                                <Badge className="bg-blue-400 text-white">{challenge.category}</Badge>
                                <Badge className="bg-blue-700 text-white">{challenge.difficulty}</Badge>
                                <span className="text-sm text-yellow-400">{challenge.points_reward} pts</span>
                              </div>
                            </div>
                            <Badge className={
                              challenge.status === 'completed' ? 'bg-yellow-500 text-blue-900' :
                              challenge.status === 'in_progress' ? 'bg-blue-400 text-white' :
                              'bg-blue-700 text-white'
                            }>
                              {challenge.status}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="messages" className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-white">Messages</h3>
                    <Button 
                      onClick={() => setShowSendMessage(true)}
                      size="sm"
                      className="bg-yellow-500 hover:bg-yellow-600 text-blue-900"
                    >
                      Send Message
                    </Button>
                  </div>
                  
                  <div className="space-y-3">
                    {playerMessages.map((message) => (
                      <Card key={message.id} className="bg-blue-900/90 border-blue-700">
                        <CardContent className="p-4">
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <Badge className="bg-blue-400 text-white">{message.message_type}</Badge>
                              <span className="text-sm text-yellow-400">
                                {new Date(message.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-white">{message.message}</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="actions" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Button 
                      onClick={() => setShowCreateChallenge(true)}
                      className="bg-yellow-500 hover:bg-yellow-600 text-blue-900 p-6 h-auto"
                    >
                      <div className="text-center">
                        <Target className="w-8 h-8 mx-auto mb-2" />
                        <p className="font-semibold">Create Individual Challenge</p>
                        <p className="text-sm opacity-90">Assign a custom challenge</p>
                      </div>
                    </Button>
                    
                    <Button 
                      onClick={() => setShowSendMessage(true)}
                      className="bg-blue-400 hover:bg-blue-500 text-white p-6 h-auto"
                    >
                      <div className="text-center">
                        <MessageSquare className="w-8 h-8 mx-auto mb-2" />
                        <p className="font-semibold">Send Message</p>
                        <p className="text-sm opacity-90">Motivate and communicate</p>
                      </div>
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      )}

      {/* Create Challenge Modal */}
      {showCreateChallenge && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 border-blue-700">
            <CardHeader>
              <CardTitle className="text-white">Create Individual Challenge</CardTitle>
              <CardDescription className="text-yellow-400">For {selectedPlayer?.full_name}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="challenge_title" className="text-white">Challenge Title</Label>
                <Input
                  id="challenge_title"
                  value={challengeData.title}
                  onChange={(e) => setChallengeData({...challengeData, title: e.target.value})}
                  placeholder="e.g., Improve shooting accuracy"
                  className="bg-blue-800 border-blue-700 text-white placeholder:text-blue-400"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="challenge_description" className="text-white">Description</Label>
                <Textarea
                  id="challenge_description"
                  value={challengeData.description}
                  onChange={(e) => setChallengeData({...challengeData, description: e.target.value})}
                  placeholder="Detailed instructions for the challenge..."
                  rows={3}
                  className="bg-blue-800 border-blue-700 text-white placeholder:text-blue-400"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="challenge_category" className="text-white">Category</Label>
                  <select
                    id="challenge_category"
                    value={challengeData.category}
                    onChange={(e) => setChallengeData({...challengeData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-blue-700 rounded-md bg-blue-800 text-white"
                  >
                    <option value="skill_development">Skill Development</option>
                    <option value="fitness">Fitness</option>
                    <option value="mental">Mental</option>
                    <option value="technical">Technical</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="challenge_difficulty" className="text-white">Difficulty</Label>
                  <select
                    id="challenge_difficulty"
                    value={challengeData.difficulty}
                    onChange={(e) => setChallengeData({...challengeData, difficulty: e.target.value})}
                    className="w-full px-3 py-2 border border-blue-700 rounded-md bg-blue-800 text-white"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="challenge_points" className="text-white">Points Reward</Label>
                <Input
                  id="challenge_points"
                  type="number"
                  value={challengeData.points_reward}
                  onChange={(e) => setChallengeData({...challengeData, points_reward: parseInt(e.target.value) || 20})}
                  min="5"
                  max="100"
                  className="bg-blue-800 border-blue-700 text-white"
                />
              </div>
              
              <div className="flex space-x-2">
                <Button onClick={createChallenge} className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-blue-900">Create Challenge</Button>
                <Button onClick={() => setShowCreateChallenge(false)} variant="outline" className="flex-1 border-blue-400 text-blue-400 hover:bg-blue-800">Cancel</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Send Message Modal */}
      {showSendMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 border-blue-700">
            <CardHeader>
              <CardTitle className="text-white">Send Message</CardTitle>
              <CardDescription className="text-yellow-400">To {selectedPlayer?.full_name}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="message_type" className="text-white">Message Type</Label>
                <select
                  id="message_type"
                  value={messageData.message_type}
                  onChange={(e) => setMessageData({...messageData, message_type: e.target.value})}
                  className="w-full px-3 py-2 border border-blue-700 rounded-md bg-blue-800 text-white"
                >
                  <option value="motivational">Motivational</option>
                  <option value="feedback">Feedback</option>
                  <option value="reminder">Reminder</option>
                  <option value="celebration">Celebration</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="message_content" className="text-white">Message</Label>
                <Textarea
                  id="message_content"
                  value={messageData.message}
                  onChange={(e) => setMessageData({...messageData, message: e.target.value})}
                  placeholder="Write your message to the player..."
                  rows={4}
                  className="bg-blue-800 border-blue-700 text-white placeholder:text-blue-400"
                />
              </div>
              
              <div className="flex space-x-2">
                <Button onClick={sendMessage} className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-blue-900">Send Message</Button>
                <Button onClick={() => setShowSendMessage(false)} variant="outline" className="flex-1 border-blue-400 text-blue-400 hover:bg-blue-800">Cancel</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}


      {/* Player Activities Modal */}
      {showPlayerActivitiesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl bg-blue-900 border border-blue-700">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-white">
                  {selectedPlayer?.full_name}'s Recent Activity
                </CardTitle>
                <Button 
                  onClick={() => setShowPlayerActivitiesModal(false)}
                  variant="ghost" 
                  size="sm"
                  className="text-white hover:bg-blue-800"
                >
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent className="max-h-96 overflow-y-auto">
              {playerActivities.length > 0 ? (
                <div className="space-y-2">
                  {playerActivities.map((activity) => (
                    <div key={activity.id || `${activity.prompt_type}-${activity.completed_at}`} className="p-3 bg-blue-800 rounded border border-blue-700">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-white font-medium">
                            {activity.prompt_type === 'challenge' 
                              ? `Challenge - ${activity.category}` 
                              : `Daily Activity - ${activity.category}`}
                          </p>
                          <p className="text-gray-300 text-sm mt-1">{activity.response_text || 'Completed'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-yellow-400 font-bold">+{activity.points_earned} pts</p>
                          <p className="text-gray-400 text-xs">
                            {new Date(activity.completed_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-400 py-8">No recent activity found</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={() => {
          confirmDialog.onConfirm?.();
          setConfirmDialog({ ...confirmDialog, open: false });
        }}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        variant={confirmDialog.variant}
      />
    </>
  );
};

export default PlayerManagementCard;
